import { Col, Row } from "antd";
import { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import AppContext from "../../../context/app/context";
import DeliveryWorkerContext from "../../../context/delivery-worker/context";
import DeliveryWorkerContextProvider from "../../../context/delivery-worker/provider";
import { ICity } from "../../../models/city/response";
import { IUpdateDeliveryWorker } from "../../../models/delivery-workers/request";
import FieldBuilder from "../../form-components/field-builder";
import MainForm from "../../form-components/main-form";
import Spin from "../../general/antd/spin";
import { useState } from "react";
import EndPoints from "../../../services/end-points";
import { languages } from "../../../utils/helpers/constants";
import { getStatusDeliveryWorker, getVehicleType } from "../../../models/delivery-workers/enum";
import defaultImage from "../../../assets/images/empty_1.png";
import FormItem from "../../general/form-item";
import Controller from "../../form-components/controller";
import ImageUploader from "../../form-components/image-uploader";
import { MediumFor } from "../../../models/medium/enum";
import OldImage from "../../form-components/old-image";
import { getTranslations } from "../../../utils/helpers/translations";
import { getUserStatus } from "../../../models/user/enum";
import { IStore } from "../../../models/store/response";
import { IDriverClassification } from "../../../models/driver-classification/response";

const Form = () => {
  const { t } = useTranslation();
  const { details, actions, loading } = useContext(DeliveryWorkerContext);
  const { id } = useParams();
  const workerStatus = [1, 2, 3];
  const vehicleType = [
    1,
    2,
    3,
    4,
    5
  ]

  const { direction } = useContext(AppContext);
  const [cities, setCities] = useState<ICity[]>([]);
  const [citiesLoading, setCitiesLoading] = useState(true);
  const [stores, setStores] = useState<IStore[]>([]);
  const [storesLoading, setStoresLoading] = useState(true);
  const [driverClassifications, setDriverClassifications] = useState<IDriverClassification[]>([]);
  const [driverClassificationsLoading, setDriverClassificationsLoading] = useState(true);
  const status = [1, 2];

  useEffect(() => {
    const getDetails = async () => {
      id && (await actions.getDetails(Number(id)));
    };
    getDetails();
  }, []);
  useEffect(() => {
    const getCities = async () => {
      try {
        const { data } = await EndPoints.city.getAllCities({
          light: true,
          perPage: -1,
        });
        setCities(data.data);
        setCitiesLoading(false);
      } catch (error) {
      } finally {
        setCitiesLoading(false);
      }
    };
    getCities();
  }, [direction]);

  useEffect(() => {
    const getStores = async () => {
      try {
        setStoresLoading(true);
        const { data } = await EndPoints.store.getAllStores({
          light: true,
          perPage: -1,
          filters: [
            { name: "isNamlieah", value: 1, operation: "eq" },
          ],
        });
        setStores(data.data);
      } catch (error) {
        console.error("Error fetching stores:", error);
      } finally {
        setStoresLoading(false);
      }
    };
    getStores();
  }, [direction]);

  useEffect(() => {
    const getDriverClassifications = async () => {
      try {
        setDriverClassificationsLoading(true);
        const { data } = await EndPoints.driverClassification.getAllDriverClassifications({
          perPage: -1,
        });
        setDriverClassifications(data.data);
      } catch (error) {
        console.error("Error fetching driver classifications:", error);
      } finally {
        setDriverClassificationsLoading(false);
      }
    };
    getDriverClassifications();
  }, [direction]);

  return (
    <MainForm
      onSubmit={async (data) => {
        details
          ? await actions.updateDeliveryWorker(details?.id, {
            ...data,
            imageId: data?.imageId?.id,
          })
          : await actions.createDeliveryWorker({
            ...data,
            imageId: data?.imageId?.id,
          });
      }}
      title={details ? t("update_delivery_worker") : t("add_delivery_worker")}
      defaultValues={
        {
          address: details?.address,
          birthDate: details?.birthDate,
          cityId: details?.city?.id,
          email: details?.user?.email,
          firstName: details?.user?.firstName,
          imageId: details?.user?.image?.id ?? undefined,
          lastName: details?.user?.lastName,
          mobile: details?.user?.cleanMobile,
          note: getTranslations(details?.user?.translations?.note) ?? null,
          phone: details?.user?.phone,
          nationalIdentityNumber: details?.nationalIdentityNumber,
          statusWorker: details?.statusWorker,
          status: details?.user?.status,
          vehicleType: details?.vehicleType,
          storeId: details?.store.id,
          driverClassificationId: details?.driverClassification?.id,
        } as IUpdateDeliveryWorker
      }
    >
      {loading.includes("details") ? (
        <div style={{ height: "50vh" }}>
          <Spin />
        </div>
      ) : (
        <>
          <Row gutter={[16, 8]}>
            <Col xs={12} sm={12} md={12} lg={8} xl={8} xxl={8}>
              <FieldBuilder
                name="firstName"
                label={t("first_name")}
                input={{ type: "text" }}
                rules={{ required: true }}
              />
            </Col>
            <Col xs={12} sm={12} md={12} lg={8} xl={8} xxl={8}>
              <FieldBuilder
                name="lastName"
                label={t("last_name")}
                input={{ type: "text" }}
                rules={{ required: true }}
              />
            </Col>
            <Col xs={12} sm={12} md={12} lg={8} xl={8} xxl={8}>
              <FieldBuilder
                name="birthDate"
                label={`${t("birth_date")}`}
                input={{ type: "date-picker", format: "YYYY-MM-DD" }}
              />
            </Col>
            <Col xs={12} sm={12} md={12} lg={8} xl={8} xxl={8}>
              <FieldBuilder
                name="mobile"
                label={t("mobile")}
                input={{ type: "text" }}
                rules={{ required: true }}
              />
            </Col>
            <Col xs={12} sm={12} md={12} lg={8} xl={8} xxl={8}>
              <FieldBuilder
                name="email"
                label={t("email")}
                input={{ type: "email" }}
                rules={{ required: false }}
              />
            </Col>
            <Col xs={12} sm={12} md={12} lg={8} xl={8} xxl={8}>
              <FieldBuilder
                name="address"
                label={t("address")}
                input={{ type: "text" }}
                rules={{ required: false }}
              />
            </Col>
            <Col xs={12} sm={12} md={12} lg={8} xl={8} xxl={8}>
              <FieldBuilder
                name="phone"
                label={t("phone")}
                input={{ type: "text" }}
                rules={{ required: false }}
              />
            </Col>
            <Col xs={12} sm={12} md={12} lg={8} xl={8} xxl={8}>
              <FieldBuilder
                name="nationalIdentityNumber"
                label={t("national_identity_number")}
                input={{ type: "text" }}
                rules={{ required: true }}
              />
            </Col>
            <Col xs={12} sm={12} md={12} lg={8} xl={8} xxl={8}>
              <FieldBuilder
                name="cityId"
                label={`${t("city")}`}
                width="large"
                rules={{ required: true }}
                input={{
                  type: "select",
                  loading: citiesLoading,
                  allowSearch: true,
                  options: cities?.map((role, index) => {
                    return {
                      key: index,
                      label: role.name,
                      value: role.id,
                    };
                  }),
                }}
              />
            </Col>
            <Col xs={12} sm={12} md={12} lg={8} xl={8} xxl={8}>
              <FieldBuilder
                name="storeId"
                label={`${t("store")}`}
                width="large"
                rules={{ required: true }}
                input={{
                  type: "select",
                  loading: storesLoading,
                  allowSearch: true,
                  options: stores?.map((store, index) => {
                    return {
                      key: index,
                      label: store.name,
                      value: store.id,
                    };
                  }),
                }}
              />
            </Col>
            <Col xs={12} sm={12} md={12} lg={8} xl={8} xxl={8}>
              <FieldBuilder
                name="driverClassificationId"
                label={`${t("driver_classification")}`}
                width="large"
                rules={{ required: true }}
                input={{
                  type: "select",
                  loading: driverClassificationsLoading,
                  allowSearch: true,
                  options: driverClassifications?.map((classification, index) => {
                    return {
                      key: index,
                      label: classification.name,
                      value: classification.id,
                    };
                  }),
                }}
              />
            </Col>
            <Col xs={12} sm={12} md={12} lg={8} xl={8} xxl={8}>
              <FieldBuilder
                name="statusWorker"
                label={`${t("worker_status")}`}
                width="large"
                rules={{ required: true }}
                input={{
                  type: "select",
                  loading: false,
                  options: workerStatus?.map((state, index) => {
                    return {
                      key: index,
                      label: t(getStatusDeliveryWorker(state)),
                      value: state,
                    };
                  }),
                }}
              />
            </Col>
            <Col xs={12} sm={12} md={12} lg={8} xl={8} xxl={8}>
              <FieldBuilder
                name="vehicleType"
                label={`${t("vehicle_type")}`}
                width="large"
                rules={{ required: true }}
                input={{
                  type: "select",
                  loading: false,
                  options: vehicleType?.map((state, index) => {
                    return {
                      key: index,
                      label: t(getVehicleType(state)),
                      value: state,
                    };
                  }),
                }}
              />
            </Col>
            <Col xs={12} sm={12} md={12} lg={8} xl={8} xxl={8}>
              <FieldBuilder
                name="status"
                label={`${t("status")}`}
                width="large"
                rules={{ required: true }}
                input={{
                  type: "select",
                  loading: false,
                  options: status?.map((state, index) => {
                    return {
                      key: index,
                      label: t(getUserStatus(state)),
                      value: state,
                    };
                  }),
                }}
              />
            </Col>
            {languages?.map((lang, index) => {
              return (
                <Col span={12}>
                  <FieldBuilder
                    key={index}
                    name={`note[${lang?.code}]`}
                    label={`${t("note")} (${lang?.name})`}
                    input={{ type: "text-area" }}
                    rules={{ required: false }}
                  />
                </Col>
              );
            })}
            <Col span={24}>
              <FormItem label={t("image")}>
                <Controller
                  name="imageId"
                  rules={{
                    required: {
                      value: false,
                      message: `${t("field_is_required_message")}`,
                    },
                  }}
                  render={({ field: { ref, ...field } }) => {
                    return (
                      <ImageUploader
                        {...field}
                        type="DRAGGER"
                        onChange={(response) => {
                          // setfileId(response);
                          field.onChange(response);
                        }}
                        imageFor={MediumFor.UserProfile}
                      />
                    );
                  }}
                />
              </FormItem>
            </Col>
            {details?.user?.image && (
              <OldImage src={details?.user?.image?.url} />
            )}
          </Row>
        </>
      )}
    </MainForm>
  );
};

const DeliveryWorkerForm = () => {
  return (
    <DeliveryWorkerContextProvider>
      <Form />
    </DeliveryWorkerContextProvider>
  );
};
export default DeliveryWorkerForm;
